// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to fetch detailed information about a single location.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { locationSchema } from '@/location/api/types';

/**
 * Schema for validating the API response when fetching a single location.
 * Transforms the response to provide a cleaner interface with the location data.
 */
const locationResponseSchema = z
  .object({
    data: locationSchema, // The location data
    metadata: responseMetadataSchema, // Request tracking metadata
  })
  .transform(({ data, ...response }) => ({ ...response, location: data }));

type LocationResponse = z.infer<typeof locationResponseSchema>;

/**
 * Fetches detailed information about a specific location.
 * Currently only supports locations of type 'SITE'.
 *
 * @param locationKey - The unique key identifier for the location
 * @returns Promise resolving to the location data and request metadata
 * @throws Will throw an error if the location is not found or if the response
 *         doesn't match the expected schema
 */
export async function getLocation(
  locationKey: string
): Promise<LocationResponse> {
  const url = `/v1/location/SITE/${locationKey}`;
  const response = await api.get(url);

  return parseResponse(locationResponseSchema, response.data, {
    schemaName: 'Location',
    endpoint: url,
  });
}
